'use client';

import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { ComponentProps, createContext, useContext } from 'react';
import { PageContext } from '@/features/app-providers/page-provider';
import type { BlogPost } from '@/features/blog/api/get-blog-posts';
import { QueryApi } from '@/features/query/api';
import { localeToLang } from '@/shared/i18n/localeToLang';
import { getLocalizedValue } from '@/shared/lib/getLocalizedValue';
import { LocalizedString } from '@/shared/model/Language';
import { ClientMDXRenderer } from '@/shared/ui/client-mdx-renderer';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Tags } from '@/shared/ui/tags';
import { TagsT } from '@/shared/ui/tags/types';

type BlogDetailContextValue = {
    data: BlogPost;
};

const BlogDetailContext = createContext<BlogDetailContextValue>({} as BlogDetailContextValue);

type BlogDetailWrapperProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const BlogDetailWrapper = ({ children, media, ...props }: BlogDetailWrapperProps) => {
    const locale = useLocale();
    const { params } = useContext(PageContext);

    const blogPostQuery = useQuery(QueryApi.Blog.queries.BlogPost(localeToLang(locale), params?.POST_SLUG));

    if (!blogPostQuery.data) {
        return null;
    }
    return (
        <BlogDetailContext.Provider value={{ data: blogPostQuery.data }}>
            <Layout
                media={{
                    ...media,
                    initial: {
                        ...media?.initial,
                    },
                }}
                {...props}
            >
                {children}
            </Layout>
        </BlogDetailContext.Provider>
    );
};

type BlogDetailTitleProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const BlogDetailTitle = ({ media, ...props }: BlogDetailTitleProps) => {
    const { data } = useContext(BlogDetailContext);
    return (
        <Layout
            element="h1"
            media={{
                ...media,
                initial: {
                    fontSize: '2rem',
                    fontWeight: 600,
                    lineHeight: '2.5rem',
                    letterSpacing: '-0.02rem',
                    ...media?.initial,
                },
            }}
            {...props}
        >
            {data.title}
        </Layout>
    );
};

type BlogDetailTagsProps = Omit<ComponentProps<typeof Tags>, 'tags'> & {
    tags: TagsT;
};
export const BlogDetailTags = ({ tags: _, appearance = 'outline', ...props }: BlogDetailTagsProps) => {
    const locale = useLocale();
    const { data } = useContext(BlogDetailContext);

    return (
        <Tags
            tags={data.tags.map((tag) => ({
                label: { [localeToLang(locale)]: tag.label },
                customColor: tag.color,
                variant: 'customColor',
            }))}
            appearance={appearance}
            {...props}
        />
    );
};

const RENDERER_BASE_MEDIA = {
    initial: {
        '& > p': {
            marginBottom: '1rem',
        },
        '& > h2, & > h3, & > h4, & > h5, & > h6': {
            marginBottom: '1rem',
            fontWeight: 600,
        },
        '& > h2': {
            fontSize: '2rem',
            lineHeight: '2.5rem',
            letterSpacing: '-0.02rem',
        },
        '& > h3': {
            fontSize: '1.75rem',
            lineHeight: '2.25rem',
            letterSpacing: '-0.0175rem',
        },
        '& > h4': {
            fontSize: '1.5rem',
            lineHeight: '2rem',
            letterSpacing: '-0.015rem',
        },
        '& > h5': {
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
            letterSpacing: '-0.0125rem',
        },
        '& > h6': {
            fontSize: '1.125rem',
            lineHeight: '1.625rem',
            letterSpacing: '-0.01125rem',
        },
        '& > p:has(+ :not(p))': {
            marginBottom: 0,
        },
    },
};

type BlogDetailContentProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const BlogDetailContent = ({ media, ...props }: BlogDetailContentProps) => {
    const { data } = useContext(BlogDetailContext);

    return (
        <Layout
            media={{
                ...RENDERER_BASE_MEDIA,
                ...media,
                initial: {
                    ...RENDERER_BASE_MEDIA.initial,
                    ...media?.initial,
                },
            }}
            {...props}
        >
            <ClientMDXRenderer content={data.content} />
        </Layout>
    );
};

type BlogDetailDescriptionProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const BlogDetailDescription = ({ media, ...props }: BlogDetailDescriptionProps) => {
    const { data } = useContext(BlogDetailContext);

    return (
        <Layout
            media={{
                ...RENDERER_BASE_MEDIA,
                ...media,
                initial: {
                    ...RENDERER_BASE_MEDIA.initial,
                    ...media?.initial,
                },
            }}
            {...props}
        >
            <ClientMDXRenderer content={data.description} />
        </Layout>
    );
};

type BlogDetailMediaProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const BlogDetailMedia = ({ media, ...props }: BlogDetailMediaProps) => {
    const { data } = useContext(BlogDetailContext);
    const mainAsset = data.assets?.[0];

    if (!mainAsset) {
        return null;
    }

    return (
        <Layout
            element="div"
            media={{
                ...media,
                initial: {
                    width: '100%',
                    maxHeight: '31.25rem',
                    borderRadius: 'var(--radius-boxes)',
                    position: 'relative',
                    overflow: 'hidden',
                    aspectRatio: '16 / 9',
                    '& > img, & > video': {
                        objectFit: 'cover',
                    },
                    ...media?.initial,
                },
            }}
            {...props}
        >
            <Media data={{ url: mainAsset.url, alt: mainAsset.alt }} />
        </Layout>
    );
};

type BlogDetailProps = {
    title: LocalizedString;
    description: LocalizedString;
    tags: TagsT;
    locale: string;
};

export const BlogDetail = ({ title, description, tags, locale }: BlogDetailProps) => {
    return (
        <Layout element="div">
            <Layout element="h1">{getLocalizedValue(title, locale)}</Layout>
            <Layout element="p">{getLocalizedValue(description, locale)}</Layout>
            <Tags tags={tags} appearance="outline" />
        </Layout>
    );
};
